
import { defineComponent, SetupContext, PropType, ref, computed, onMounted } from "vue";
import { FridgeDetailsEconaviInfoListItem } from "@/types/fridgeDetails";
import NativeClient from "@/client/native-client";

export default defineComponent({
  name: "FridgeEconaviContentItem",
  props: {
    /** 表示する文字列 */
    info: {
      type: Object as PropType<FridgeDetailsEconaviInfoListItem>,
      required: true
    }
  },
  setup(props, context: SetupContext) {
    NativeClient.sendDebugLog("FridgeEconaviContentItem setup start");
    
    const firstLegendTitle = "エコナビ運転";
    const secondLegendTitle = "通常運転";
    const ratioTitleText = "エコナビ運転率";
    const percentText = "%";
    const noteText = "※数字はあくまで目安です。";
    const baseCanvasSize = { width: 186, height: 186 };
    const baseRadius = 81;
    const canvasSize = computed(() => {
      const scale = window.devicePixelRatio;
      NativeClient.sendDebugLog("FridgeEconaviContentItem devicePixelRatio = " + scale);
      return { width: baseCanvasSize.width * scale, height: baseCanvasSize.height * scale };
    });

    const canvas = ref<HTMLCanvasElement>();
    const drawEconavi = () => {
      if (canvas.value === undefined) {
        NativeClient.sendDebugLog("FridgeEconaviContentItem drawEconavi canvas.value is undefined");
        return;
      }
      const targetContext = canvas.value.getContext("2d");
      if (targetContext == null) {
        NativeClient.sendDebugLog("FridgeEconaviContentItem drawEconavi targetContext is null");
        return;
      }
      const scale = window.devicePixelRatio;
      NativeClient.sendDebugLog("FridgeEconaviContentItem drawEconavi = " + scale);
      const realCanvasSize = { width: baseCanvasSize.width * scale, height: baseCanvasSize.height * scale };
      targetContext.clearRect(0, 0, realCanvasSize.width, realCanvasSize.height);
      const oneArcPartsAngle = 2 * Math.PI / 48 - 1 * Math.PI / 180;  // 一周を48分割（= 24 * 60 / 30）
      const startAngle = 0.5 * 2 * Math.PI / 180;
      const intervalAngle = 1 * Math.PI / 180
      let currentAngle = -1 * Math.PI / 2;
      const tableAngle = [];
      const initial =  { beginAngle: startAngle + currentAngle, endAngle: startAngle + currentAngle + oneArcPartsAngle};
      tableAngle.push(initial);
      for (let index = 1; index < 48; index ++) {
        currentAngle = tableAngle[index - 1].endAngle + intervalAngle;
        const tempAngle = { beginAngle: currentAngle, endAngle: currentAngle + oneArcPartsAngle};
        tableAngle.push(tempAngle);
      }

      const fanWidth = 10;
      const realRadius = baseRadius * scale;
      const centerX = realCanvasSize.width / 2;
      const centerY = realCanvasSize.height / 2;
      const outerRadius = realRadius;
      const innerRadius = outerRadius - fanWidth * scale;
      for (const element of tableAngle) {
        targetContext.beginPath();
        targetContext.arc(centerX, centerY, outerRadius, element.beginAngle, element.endAngle, false);
        targetContext.arc(centerX, centerY, innerRadius, element.endAngle, element.beginAngle, true);
        targetContext.closePath();
        targetContext.fillStyle = "rgba(228, 228, 228, 1.0)";
        targetContext.fill();
      }
      // 時間のメモリ
      const strokeColor = "#707070";
      const linewWidth = 1 * scale;

      // 横棒
      targetContext.beginPath();
      targetContext.moveTo( (fanWidth + 16) * scale, centerY);
      targetContext.lineTo(realCanvasSize.width - (fanWidth + 16) * scale, centerY);
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = linewWidth;
      targetContext.stroke();

      // 時間のメモリ
      const lineLength = 8 * scale;

      // 0時付近
      targetContext.beginPath();
      targetContext.moveTo(centerX, 0);
      targetContext.lineTo(centerX, lineLength);
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = linewWidth;
      targetContext.stroke();

      // 6時付近
      targetContext.beginPath();
      targetContext.moveTo(realCanvasSize.width - lineLength, centerY);
      targetContext.lineTo(realCanvasSize.width, centerY);
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = linewWidth;
      targetContext.stroke();

      // 12時付近
      targetContext.beginPath();
      targetContext.moveTo(centerX, realCanvasSize.width - lineLength);
      targetContext.lineTo(centerX, realCanvasSize.width);
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = linewWidth;
      targetContext.stroke();

      // 18時付近
      targetContext.beginPath();
      targetContext.moveTo( 0, centerY);
      targetContext.lineTo(lineLength, centerY);
      targetContext.strokeStyle = strokeColor;
      targetContext.lineWidth = linewWidth;
      targetContext.stroke();

      // ON状態のみ抽出
      NativeClient.sendDebugLog("FridgeEconaviContentItem info = " + JSON.stringify(props.info));
      if (props.info.econavi_list_time.length == 0) {
        // データがないため、表示しない
        return;
      }
      const econaviOnePartsAngle = 2 * Math.PI / 48;
      for (const item of props.info.econavi_list_time) {
        const status = item.operation_status.toUpperCase()
        if (status != null) {
                  // 時分を角度に変換（分は、0か30しかない想定）
          const hmsArray = item.date_time.split(":");
          NativeClient.sendDebugLog("FridgeEconaviContentItem hmsArray = " + JSON.stringify(hmsArray));
          if (hmsArray.length < 3) {
            // hh:mm:ssが揃っていないので無視する
            continue;
          }
          const thirtyMinutsParDay = Math.floor((parseInt(hmsArray[0]) * 60 + parseInt(hmsArray[1])) / 30);
          const beginAngle = thirtyMinutsParDay * econaviOnePartsAngle - Math.PI / 2;  // originは、X軸右側なので、Y軸上側にするため
          const endAngle = beginAngle + econaviOnePartsAngle;
          targetContext.beginPath();
          targetContext.arc(centerX, centerY, outerRadius, beginAngle, endAngle, false);
          targetContext.arc(centerX, centerY, innerRadius, endAngle, beginAngle, true);
          targetContext.closePath();
          if (status == "ON") {
            targetContext.fillStyle = "rgba(92,214,96,0.5)";
          } else if (status == "OFF") {
            targetContext.fillStyle = "rgba(183,183,183,0.5)";
          } else {
            targetContext.fillStyle = "rgba(183,183,183,0.0)";
          }
          targetContext.fill();
        }
      }
    };

    // 運転率の有効／無効
    const isValidRation = computed(() => {
      if (props.info.operating_rate == "-" || props.info.operating_rate == "") {
        return false;
      }
      return true;
    });

    onMounted(() => {
      drawEconavi();
    });

    return {
      canvas,
      canvasSize,
      firstLegendTitle,
      secondLegendTitle,
      isValidRation,
      ratioTitleText,
      percentText,
      noteText
    };
  }
});
